import Enumerable from "linq";
import Moment from "moment";
import { IConfigurationRegistry } from "../../../../base/domain/configuration";
import { instanceOfIRectangleCoordinatable, IContentSource } from "../../../../base/domain/drawing";
import { ObjectUtility } from "../../../../base/utilities";
import { NewObjectKeys, IConfiguration } from "../../../domain/configuration";
import {
  IEditableFormatRepository,
  EditableFormatSummary,
  IEditableFormatStructure,
  IEditableFormatObject,
  EditHistorySearchParameter,
  IEditableFormatOperation,
  RevertFormatEditionResult,
  FormatStructureEditor,
  FormatRegistrationResult,
} from "../../../domain/editableFormat";
import {
  EditableFormatImageSource,
  EditableFormatLayerImageSource,
  EditableFormatTemporaryImageSource,
} from "../../../domain/editableFormat/sources";
import { IImageDrawableObject } from "../../../domain/editableFormat/objects";
import {
  IEditableFormatBean,
  IEditableFormatOperationBean,
  IFormatObjectBean,
  IFormatRegisterResultBean,
  IXElementContentsObjectBean,
} from "./beans";
import { EditableFormatRepositoryBase } from "./EditableFormatRepositoryBase";

export class EditableFormatRepository extends EditableFormatRepositoryBase implements IEditableFormatRepository {
  constructor(getToken: () => string, configurationRegistry: IConfigurationRegistry<IConfiguration>) {
    super(getToken, configurationRegistry);
  }

  async getNewFormatStructure(version: string): Promise<IEditableFormatStructure> {
    const url = "api/format-editor/format/new";
    const result = await this.get<IFormatObjectBean>(
      url,
      {
        params: {
          version: version,
        }
      });
    return this._translatorFactory.create().toFormatStructure(result.data);
  }

  async getFormatStructure(id: number): Promise<IEditableFormatStructure> {
    const url = "api/format-editor/format/" + id + "/edit";
    const result = await this.get<IFormatObjectBean>(url);
    return this._translatorFactory.create().toFormatStructure(result.data);
  }

  async getNewCopiedFormatStructure(id: number, archiveId: number): Promise<IEditableFormatStructure> {
    const url = "api/format-editor/format/" + id + "/copy";
    const result = await this.get<IFormatObjectBean>(
      url,
      {
        params: {
          archiveId: archiveId,
        }
      });
      
    const format = this._translatorFactory.create().toFormatStructure(result.data);
    // 貼り付け画像をキャッシュテーブルから取得するよう情報を書き換える
    Enumerable.from(format.pages)
      .forEach(async p => Enumerable.from(p.objects)
        .forEach(async o => {
          if (o.elementName === "image" || o.elementName === "imagearea") {
            const imageObj = (o as IImageDrawableObject);
            imageObj.temporaryImageContent.isEnable = !!imageObj.imageContent.guid;
            imageObj.temporaryImageContent.guid = imageObj.imageContent.guid;
          }
        }));

    return format;
  }

  async getNewCopiedLatestBackgroundImageGuid(id: number, imageGuid: string): Promise<string> {
    const url = "api/format-editor/format/" + id + "/background/" + imageGuid + "/copy";
    const result = await this.get<string>(url);
    return result.data;
  }

  createNewFormatObject(key: NewObjectKeys, formatId: number): IEditableFormatObject {
    const result = this._translatorFactory.create().toFormatPageObject(this.getNewFormatObjectBean(key), formatId);
    if (instanceOfIRectangleCoordinatable(result)) {
      // 位置は指定されていても意味がないので(0,0)に.
      result.location.x = 0;
      result.location.y = 0;
      // サイズは0だと配置中のオフセット計算で0除算が発生するので20x20で補完する.(0以上ならそのまま)
      result.size.width = (0 < result.size.width) ? result.size.width : 20;
      result.size.height = (0 < result.size.height) ? result.size.height : 20;
    }
    return result;
  }

  private getNewFormatObjectBean(key: NewObjectKeys): IXElementContentsObjectBean {
    const configuration = this._configurationRegistry.getConfigurationService().getConfiguration();
    return ObjectUtility.copyDeeply(configuration.formatEditor.behavior.format.editor.newObjects[key].source);
  }

  async getUserEditedFormats(id: string): Promise<EditableFormatSummary[]> {
    const url = "api/format-editor/format/edited";
    const result = await this.get<IEditableFormatBean[]>(
      url,
      {
        params: {
          userId: id,
        },
      });
    return this._translatorFactory.create().toFormats(() => this.token, result.data);
  }

  async getNoneDisplayFormats(): Promise<EditableFormatSummary[]> {
    const url = "api/format-editor/format/none-display";
    const result = await this.get<IEditableFormatBean[]>(url);
    return this._translatorFactory.create().toFormats(() => this.token, result.data);
  }

  async getDeletedFormats(): Promise<EditableFormatSummary[]> {
    const url = "api/format-editor/format/deleted";
    const result = await this.get<IEditableFormatBean[]>(url);
    return this._translatorFactory.create().toFormats(() => this.token, result.data);
  }

  async getUnsynchronizedFormats(): Promise<EditableFormatSummary[]> {
    const url = "api/format-sync/list";
    const result = await this.get<IEditableFormatBean[]>(url);
    return this._translatorFactory.create().toFormats(() => this.token, result.data);
  }

  async getEditHistory(param: EditHistorySearchParameter): Promise<IEditableFormatOperation[]> {
    const url = "api/format-editor/format/operations";
    const query = {
      from: this.toDateString(param.dateDuration.from),
      to: this.toDateString(param.dateDuration.to),
    };
    const result = await this.post<IEditableFormatOperationBean[]>(url, query);
    return this._translatorFactory.create().toFormatOperations(result.data);
  }

  private toDateString(source: Date): string {
    return source ? Moment(source).format("YYYYMMDD") : null;
  }

  async registerFormatStructure(format: FormatStructureEditor, isTemporary: boolean): Promise<FormatRegistrationResult> {
    const url = "api/format-editor/format/save";
    const formatBean = this._translatorFactory.create().toFormatObjectBean(format.getSource(), isTemporary);
    console.debug(formatBean);
    const result = await this.post(url, formatBean);
    console.debug(result.data);
    return this.toFormatRegistrationResult(result.data);
  }

  private toFormatRegistrationResult(bean: IFormatRegisterResultBean): FormatRegistrationResult {
    return new FormatRegistrationResult(
      bean.isOk,
      bean.isOk
        ? {
          id: bean.formatId,
          archiveId: bean.archiveId,
        }
        : {
          isDeleted: bean.items.isDeleted,
          edition: bean.items.edition,
          updated: {
            userName: bean.items.updateUserName,
            teminal: bean.items.updateTerminal,
            dateTime: bean.items.updateDateTime,
          },
        }
    );
  }

  async revertFormatEdition(id: number, archiveId: number): Promise<RevertFormatEditionResult> {
    const url = "api/format-editor/format/" + id + "/revert";
    const result = await this.get<RevertFormatEditionResult>(
      url,
      {
        params: {
          revertArchiveId: archiveId,
        }
      });
    return result.data;
  }

  async reviveFormat(id: number, parent: number): Promise<void> {
    const url = "api/format-editor/format/" + id + "/revive";
    await this.get<FormatRegistrationResult>(
      url,
      {
        params: {
          parentId: parent,
        }
      });
  }

  async synchronizeFormats(formats: EditableFormatSummary[]): Promise<void> {
    const url = "api/format-sync/synchronize";
    const query = Enumerable.from(formats).select(s => {
      return {
        formatId: s.id,
        archiveId: s.archiveId,
      };
    }).toArray();
    console.debug(query);
    await this.post<void>(url, query);
    return;
  }

  async uploadImageFile(contents: string): Promise<string> {
    const url = "api/format-editor/temporary/image-save";
    const query = {
      image: contents,
    };
    const result = await this.post<string>(url, query);
    return result.data;
  }

  async uploadReferenceFile(fileName: string, contents: string): Promise<string> {
    const url = "api/format-editor/temporary/reference-save";
    const query = {
      fileName: fileName,
      contents: contents,
    };
    const result = await this.post<string>(url, query);
    return result.data;
  }

  async getNewTemporaryImageGuid(imageGuid: string): Promise<string> {
    const url = "api/format-editor/temporary/image/" + imageGuid + "/copy";
    const result = await this.get<string>(url);
    return result.data;
  }

  getImageSource(formatId: number, imageGuid: string): IContentSource {
    return new EditableFormatImageSource(() => this.token, formatId, null, 1, imageGuid);
  }

  getLayerImageSource(formatId: number, imageGuid: string): IContentSource {
    return new EditableFormatLayerImageSource(() => this.token, formatId, imageGuid);
  }

  getTemporaryImageSource(imageGuid: string): IContentSource {
    return new EditableFormatTemporaryImageSource(() => this.token, 2, imageGuid);
  }

  async setEveryoneEditable(formatId: number, isEditable: boolean): Promise<void> {
    await this.post(
      "api/format-editor/format/" + formatId + "/everyone/editable/set",
      { enable: isEditable });
  }

  async getEditable(formatId: number): Promise<boolean> {
    return (await this.get<boolean>("api/format-editor/format/" + formatId + "/editable/get")).data;
  }

  async getAccessible(formatId: number): Promise<boolean> {
    return (await this.get<boolean>("api/format-editor/format/" + formatId + "/accessible/get")).data;
  }
}
